<template>
  <div class="flex flex-wrap items-center gap-2">
    <span
      class="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5"
      :style="badgeStyle"
    >
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M14.5 3.5l6 6-3 3-6-6 3-3z" />
          <path d="M9.5 8.5l6 6-3 3-6-6 3-3z" transform="rotate(90 12 12)" />
          <path d="M3 21l6-6" />
          <path d="M21 21l-6-6" />
        </svg>
      <span
        class="min-w-0 truncate rounded-sm font-display text-sm font-semibold tracking-[0.04em] outline-none transition-colors focus-visible:bg-bg-surface/40"
        :contenteditable="canEdit"
        spellcheck="false"
        data-room-name="true"
        :title="canEdit ? messages.room.renameTitle(room.name) : room.name"
        :aria-label="canEdit ? messages.room.editableNameAria(room.name) : messages.room.nameAria(room.name)"
        :style="{ color: accentColor }"
        @blur="onNameCommit($event)"
        @keydown="onNameKeydown($event)"
      >{{ room.name }}</span>
    </span>

    <button
      type="button"
      class="inline-flex h-7 cursor-pointer items-center gap-1 rounded-md border px-2 text-xs font-semibold transition-[color,border-color,background-color] duration-120 ease-standard focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2"
      :class="groupsVertical
        ? 'border-accent bg-accent/10 text-accent'
        : 'border-border bg-bg-elevated text-text-secondary hover:border-accent/60 hover:text-accent'"
      :aria-label="groupLayoutLabel"
      :aria-pressed="groupsVertical"
      :title="groupLayoutLabel"
      @click="emit('toggle-group-layout')"
    >
      <AppIcon
        :name="groupsVertical ? IconName.LayoutDetail : IconName.LayoutCompact"
        :size="12"
      />
      <span>{{ groupsVertical ? messages.room.vertical : messages.room.horizontal }}</span>
    </button>

    <div v-if="!isPoolRoom && canEdit" class="flex items-center gap-0.5">
      <button
        type="button"
        class="inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-sm border border-border bg-transparent text-text-secondary transition-[color,border-color] duration-120 ease-standard hover:text-accent hover:border-accent focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:text-text-secondary disabled:hover:border-border"
        :disabled="isFirstRoom"
        :aria-label="messages.room.moveUp(room.name)"
        :title="messages.room.moveUpTitle"
        @click="onMoveUp"
      >
        <AppIcon :name="IconName.ChevronUp" :size="12" />
      </button>
      <button
        type="button"
        class="inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-sm border border-border bg-transparent text-text-secondary transition-[color,border-color] duration-120 ease-standard hover:text-accent hover:border-accent focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:text-text-secondary disabled:hover:border-border"
        :disabled="isLastRoom"
        :aria-label="messages.room.moveDown(room.name)"
        :title="messages.room.moveDownTitle"
        @click="onMoveDown"
      >
        <AppIcon :name="IconName.ChevronDown" :size="12" />
      </button>
    </div>

    <div v-if="!isPoolRoom && canEdit && mode === BoardMode.Live" class="flex items-center gap-1">
      <button
        type="button"
        class="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border border-border bg-bg-elevated text-text-secondary transition-[color,border-color,background-color] duration-120 ease-standard hover:border-danger hover:text-danger focus-visible:outline-2 focus-visible:outline-danger focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-border disabled:hover:text-text-secondary"
        :disabled="store.rooms.length <= 1"
        :aria-label="messages.room.removeAria(room.name)"
        :title="messages.room.removeTitle"
        @click="onRemoveRoom"
      >
        <AppIcon :name="IconName.X" :size="14" />
      </button>
    </div>

    <!--
      Plan 04 — stage chip. Hidden on the live board and on
      cross-stage rooms (Pool). Officers read the timeline
      slot at a glance so the per-stage layout reads as a
      stacked timeline rather than a flat list of rooms.
    -->
    <span
      v-if="stage !== null"
      class="inline-flex items-center gap-1 rounded-full border border-border bg-bg-elevated px-2 py-0.5 text-xs font-semibold tracking-wide uppercase text-text-secondary"
      :title="messages.room.stageTitle(stage.name)"
      :aria-label="messages.room.stageAria(stage.name)"
    >
      <span aria-hidden="true">{{ messages.room.stage }}</span>
      <span>{{ stage.name }}</span>
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { AppIcon, IconName } from '@/shared/ui'
import { boardEn } from './locales/en.ts'
import { boardRu } from './locales/ru.ts'
import { POOL_ROOM_ID, useGvgPlannerStore } from '../../stores/gvgPlannerStore'
import { isAtLastMovablePosition } from '../../lib/roomBounds'
import type { Room } from '../../types'
import { BoardMode, RoomAccent } from '../../types'

export interface RoomHeaderProps {
  readonly room: Room
  /**
   * Plan 02 — `'live'` (default) renders the room's name editable, hides
   * nothing, and lets the store mutations hit the live board. `'raid'`
   * (when a `RaidBoard` is selected) suppresses every mutation
   * affordance: no reorder, no remove, no rename. The Pool room is
   * already hidden for both modes (the store's Pool invariants still
   * apply, but the raid snapshot can't mutate the live Pool either).
   */
  readonly mode: BoardMode
  /**
   * Pre-computed by `RoomSection` so the same `canEdit` literal that the
   * test at `raids.test.ts` pins (officer AND `boardMode === Live`) keeps
   * living in one place.
   */
  readonly canEdit: boolean
  /**
   * View-only group orientation. The button is available to every viewer
   * because it changes presentation only and never dispatches board data.
   */
  readonly groupsVertical: boolean
  /**
   * Plan 04 — informational only. When the planner is in raid-mode and
   * a stage is selected, the room header renders a small "Stage N —
   * <kind>" chip so the user can see which timeline slot the rooms
   * underneath belong to. Cross-stage rooms (Pool) show no chip.
   */
  readonly stage: { readonly id: string; readonly kind: string; readonly name: string } | null
}

export interface RoomHeaderEmits {
  (e: 'name-commit', next: string): void
  (e: 'move-up', roomId: string): void
  (e: 'move-down', roomId: string): void
  (e: 'remove', roomId: string): void
  (e: 'toggle-group-layout'): void
}

const props = defineProps<RoomHeaderProps>()
const emit = defineEmits<RoomHeaderEmits>()

const catalog = defineLocaleCatalog({ en: boardEn, ru: boardRu })
const messages = useLocaleMessages(catalog)
const store = useGvgPlannerStore()

// Maps a room's accent hue to the CSS custom property that drives every
// colour reference in the header (the rounded badge, the glow, and the room
// name text). Each accent gets a distinct semantic colour so a freshly added
// room isn't visually identical to the others.
//
// Purple is decorative-only: it predates the role → colour palette collapse
// (the four-path system used purple as a real sub-path, but the role-tinted
// palette only ships Tank / Dps / Heal now). Since no purple role token exists
// any more, Purple rooms fall back to the brand accent. `RoomAccent.Purple` is
// still emitted by `addRoom` and validated by `isValidRoom`, so the enum member
// stays — this map just chooses a sensible visual substitute.
const ROOM_ACCENT_VAR: Readonly<Record<RoomAccent, string>> = {
  [RoomAccent.Red]: '--color-danger',
  [RoomAccent.Blue]: '--color-info',
  [RoomAccent.Green]: '--color-success',
  [RoomAccent.Purple]: '--color-accent',
}

const accentVar = computed<string>(() => `var(${ROOM_ACCENT_VAR[props.room.accent]})`)

const accentColor = computed<string>(() => accentVar.value)

const groupLayoutLabel = computed<string>(() => {
  const current = props.groupsVertical ? messages.value.room.vertical : messages.value.room.horizontal
  const target = props.groupsVertical ? messages.value.room.horizontal : messages.value.room.vertical
  return messages.value.room.layoutDescription(props.room.name, current, target)
})

const badgeStyle = computed<Record<string, string>>(() => {
  const accent = accentVar.value
  return {
    backgroundColor: `color-mix(in srgb, ${accent} 15%, transparent)`,
    borderColor: `color-mix(in srgb, ${accent} 60%, transparent)`,
    color: accent,
    boxShadow: `0 0 20px color-mix(in srgb, ${accent} 35%, transparent)`,
  }
})

const roomIndex = computed<number>(() =>
  store.currentRooms.findIndex((r) => r.id === props.room.id),
)

const isFirstRoom = computed<boolean>(() => roomIndex.value <= 0)
// Delegates the Pool-wall boundary check to the shared helper so the store
// (`moveRoom`) and this disabled-arrow rule stay in lockstep. Reads
// `currentRooms` (the raid-aware mirror) so the up/down arrows also work
// on the raid board, where rendered room ids are snapshot stable ids
// that never appear in `store.rooms`.
const isLastRoom = computed<boolean>(() =>
  isAtLastMovablePosition(store.currentRooms, roomIndex.value),
)
// The Pool room is a reserved fixed-shape room: it cannot be removed, it
// cannot be reordered, and its two groups (Signed, Other) cannot be added
// to or removed from. The store defends each constraint; we additionally
// hide the matching UI so the user gets no misleading affordances.
const isPoolRoom = computed<boolean>(() => props.room.id === POOL_ROOM_ID)

// ----- mutation handlers -----
function onMoveUp(): void {
  if (!props.canEdit) return
  if (roomIndex.value <= 0) return
  emit('move-up', props.room.id)
}

function onMoveDown(): void {
  if (!props.canEdit) return
  if (roomIndex.value < 0) return
  emit('move-down', props.room.id)
}

// Room removal.
function onRemoveRoom(): void {
  if (!props.canEdit) return
  // Mirrors the destructive-action confirmation used by the global reset
  // button: warn the user that removing a room sends its players back to the
  // pool. The store is a no-op when only one room remains, and the button is
  // disabled in that case, so this confirmation is effectively unreachable
  // for the last room — defensive `if` guards that anyway.
  if (store.rooms.length <= 1) return
  const ok = window.confirm(messages.value.room.removeConfirm(props.room.name))
  if (!ok) return
  emit('remove', props.room.id)
}

// Room-name inline edit (contenteditable). Enter commits and blurs; Escape
// reverts the text and blurs without committing; blur commits whatever's in
// the field at that moment.
function onNameKeydown(event: KeyboardEvent): void {
  if (event.key === 'Enter') {
    event.preventDefault()
    ;(event.target as HTMLElement).blur()
    return
  }
  if (event.key === 'Escape') {
    event.preventDefault()
    const el = event.target as HTMLElement
    el.innerText = props.room.name
    el.blur()
  }
}

function onNameCommit(event: FocusEvent): void {
  const el = event.target as HTMLElement
  const next = el.innerText.trim()
  // The store falls back to `Room <index+1>` on empty input, so an empty
  // commit intentionally cascades through the store. We just refuse to no-op
  // re-renames.
  if (next && next !== props.room.name) {
    emit('name-commit', next)
  } else {
    el.innerText = props.room.name
  }
}
</script>
